<template>
    <AmModal
        :open="open"
        size="narrow"
        title="Поделиться ссылкой"
        :subtitle="`Выбрано ${messageIds.length} ${messageWord} из этого диалога.`"
        @close="onClose"
    >
            <div v-if="!result" class="modal-form">
                <label class="field">
                    <span class="field-label">Название (опционально)</span>
                    <input
                        v-model="title"
                        type="text"
                        class="input"
                        maxlength="120"
                        placeholder="Например: «Динамика Haval Jolion · фев 2026»"
                    />
                </label>

                <fieldset class="field">
                    <legend class="field-label">Режим</legend>
                    <label class="radio">
                        <input v-model="mode" type="radio" value="read_only" />
                        <span>
                            <strong>Только просмотр</strong>
                            <em>Получатель видит ответы, но не может продолжить ветку.</em>
                        </span>
                    </label>
                    <label class="radio">
                        <input v-model="mode" type="radio" value="forkable" />
                        <span>
                            <strong>Разрешить продолжить ветку</strong>
                            <em>Получатель сможет клонировать диалог к себе и задавать новые вопросы.</em>
                        </span>
                    </label>
                </fieldset>

                <fieldset class="field">
                    <legend class="field-label">Срок действия</legend>
                    <div class="ttl-row">
                        <button
                            v-for="opt in TTL_OPTIONS"
                            :key="opt.id"
                            type="button"
                            class="ttl-chip"
                            :class="{ 'ttl-chip--on': ttl === opt.id }"
                            @click="ttl = opt.id"
                        >
                            {{ opt.label }}
                        </button>
                    </div>
                    <input
                        v-if="ttl === 'custom'"
                        v-model="customDate"
                        type="datetime-local"
                        class="input"
                    />
                </fieldset>

                <div v-if="error" class="error-line">{{ error }}</div>

                <div class="modal-actions">
                    <button type="button" class="ghost-btn" @click="onClose">
                        Отмена
                    </button>
                    <button
                        type="button"
                        class="primary-btn"
                        :disabled="creating || messageIds.length === 0"
                        @click="onCreate"
                    >
                        <ChatIcon name="link" :size="13" />
                        {{ creating ? 'Создаю…' : 'Создать ссылку' }}
                    </button>
                </div>
            </div>

            <div v-else class="modal-form">
                <p class="success-line">Ссылка готова — отправьте её адресату.</p>
                <div class="url-row">
                    <input :value="shareUrl" readonly class="input" />
                    <button type="button" class="primary-btn" @click="copyUrl">
                        <ChatIcon name="copy" :size="13" />
                        {{ copied ? 'Скопировано' : 'Копировать' }}
                    </button>
                </div>
                <div class="result-meta">
                    Режим:
                    <strong>{{ result.mode === 'forkable' ? 'разрешено продолжать' : 'только просмотр' }}</strong>
                    <span v-if="result.expiresAt">
                        · истекает {{ formatDate(result.expiresAt) }}
                    </span>
                    <span v-else>· без срока</span>
                </div>
                <div class="modal-actions">
                    <button type="button" class="ghost-btn" @click="openPreview">
                        Открыть превью
                    </button>
                    <button type="button" class="primary-btn" @click="onClose">
                        Готово
                    </button>
                </div>
            </div>
    </AmModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import AmModal from '@/components/ui/AmModal.vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import { createShare } from '@/utils/chatApi'

const props = defineProps({
    open: { type: Boolean, default: false },
    threadExternalId: { type: String, default: '' },
    messageIds: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'created'])

const title = ref('')
const mode = ref('forkable')
const ttl = ref('30d')
const customDate = ref('')
const creating = ref(false)
const error = ref('')
const result = ref(null)
const copied = ref(false)

const TTL_OPTIONS = [
    { id: 'none', label: 'Без срока' },
    { id: '7d', label: '7 дней' },
    { id: '30d', label: '30 дней' },
    { id: 'custom', label: 'Своя дата' },
]

watch(() => props.open, (val) => {
    if (val) {
        title.value = ''
        mode.value = 'forkable'
        ttl.value = '30d'
        customDate.value = ''
        creating.value = false
        error.value = ''
        result.value = null
        copied.value = false
    }
})

const shareUrl = computed(() => {
    if (!result.value?.token) return ''
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return `${origin}/chat/share/${result.value.token}`
})

function computeExpiresAt() {
    if (ttl.value === 'none') return null
    if (ttl.value === '7d') return new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString()
    if (ttl.value === '30d') return new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString()
    if (ttl.value === 'custom' && customDate.value) {
        const d = new Date(customDate.value)
        if (Number.isNaN(d.getTime())) return null
        return d.toISOString()
    }
    return null
}

async function onCreate() {
    if (creating.value) return
    creating.value = true
    error.value = ''
    try {
        const data = await createShare({
            threadExternalId: props.threadExternalId,
            messageIds: props.messageIds,
            mode: mode.value,
            title: title.value.trim() || null,
            expiresAt: computeExpiresAt(),
        })
        result.value = data
        emit('created', data)
    } catch (e) {
        error.value = e?.message || 'Не удалось создать ссылку. Попробуйте ещё раз.'
    } finally {
        creating.value = false
    }
}

function copyUrl() {
    if (!shareUrl.value) return
    try {
        navigator.clipboard.writeText(shareUrl.value)
        copied.value = true
        setTimeout(() => (copied.value = false), 1800)
    } catch (e) {
        // ignore
    }
}

function openPreview() {
    if (!result.value?.token) return
    window.open(`/chat/share/${result.value.token}`, '_blank', 'noopener')
}

function onClose() {
    emit('close')
}

function plural(n, forms) {
    const abs = Math.abs(n) % 100
    if (abs >= 11 && abs <= 14) return forms[2]
    const last = abs % 10
    if (last === 1) return forms[0]
    if (last >= 2 && last <= 4) return forms[1]
    return forms[2]
}

const messageWord = computed(() => plural(props.messageIds.length, ['сообщения', 'сообщений', 'сообщений']))

function formatDate(iso) {
    if (!iso) return ''
    try {
        return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
    } catch (e) { return '' }
}
</script>

<style scoped>
.modal-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    border: 0;
    padding: 0;
    margin: 0;
}

.field-label {
    font-size: 11.5px;
    font-weight: 600;
    color: #5f5f5f;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.input {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 8px 10px;
    font-family: inherit;
    font-size: 13px;
    color: #23282C;
    width: 100%;
    box-sizing: border-box;
    background: white;
}

.input:focus {
    outline: none;
    border-color: #23282C;
}

.radio {
    display: flex;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    cursor: pointer;
    align-items: flex-start;
}

.radio:hover { background: #f7f7f7; }

.radio input { margin-top: 3px; }

.radio strong {
    display: block;
    font-size: 13px;
    color: #23282C;
    font-weight: 600;
}

.radio em {
    display: block;
    font-style: normal;
    font-size: 11.5px;
    color: #888888;
    margin-top: 2px;
}

.ttl-row {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.ttl-chip {
    padding: 7px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    font-family: inherit;
    font-size: 12px;
    color: #23282C;
    cursor: pointer;
}

.ttl-chip:hover { border-color: #c0c0c0; }

.ttl-chip--on {
    background: #23282C;
    color: white;
    border-color: #23282C;
}

.error-line {
    color: #C0050F;
    font-size: 12px;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 4px;
}

.ghost-btn {
    background: transparent;
    border: 1px solid #e0e0e0;
    padding: 8px 14px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    color: #23282C;
    cursor: pointer;
}

.ghost-btn:hover { background: #f0f0f0; }

.primary-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #E30613;
    color: white;
    border: 0;
    padding: 8px 14px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.primary-btn:hover { background: #C0050F; }
.primary-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.success-line {
    margin: 0;
    color: #0F6E56;
    font-size: 13px;
    font-weight: 500;
}

.url-row {
    display: flex;
    gap: 8px;
    align-items: stretch;
}

.url-row .input {
    flex: 1;
    background: #f7f7f7;
}

.result-meta {
    font-size: 12px;
    color: #5f5f5f;
}
</style>
